import { memo, useCallback } from 'react';

import { TagIcon, XIcon } from '@air/design-icons';
import { Chip } from '@air/primitive-chip';
import { IconButton } from '@air/primitive-icon-button';

export type WorkspaceAgentTagChipProps = {
  'data-testid'?: string;
  label: string;
  /**
   * Supplying a handler is what makes the chip removable. A separate `removable` flag would allow a
   * chip that offers a button it cannot honour. Receives the label so a list of chips can share one
   * handler instead of building a closure per chip.
   */
  onRemove?: (label: string) => void;
};

/**
 * A metadata tag. Not to be confused with a pill (`PrivateWorkspaceAgentPanelMessagePillChip`), which
 * references an asset or board and resolves through the clips provider — the two look alike and share
 * nothing.
 */
export const WorkspaceAgentTagChip = memo(({ 'data-testid': testId, label, onRemove }: WorkspaceAgentTagChipProps) => {
  const handleRemove = useCallback(() => onRemove?.(label), [label, onRemove]);

  return (
    // `asChild` because the chip sits mid-sentence and the primitive renders a div.
    <Chip asChild color="gray" data-testid={testId} shape="rounded" size="small">
      {/* `align-middle` matters when a chip sits mid-sentence: an inline-flex box otherwise takes its
          baseline from the tag icon's bottom edge and hangs below the line. A pill is a self-contained
          object, so centring it against the x-height is the right relationship to the copy either way. */}
      <span className="max-w-full align-middle">
        <TagIcon className="shrink-0" size="xs" />
        <span className="truncate">{label}</span>
        {onRemove ? (
          <IconButton
            adsIcon
            appearance="ghost"
            // `IconButton` ships no focus style of its own, so the panel's ring is restated rather than lost.
            className="focus-visible:outline-none ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-altitude-8"
            color="gray"
            // biome-ignore lint/plugin: PLA-1724 — pre-existing debt, not introduced by the icon migration
            data-testid={`WORKSPACE_AGENT_TAG_CHIP_REMOVE_BTN-${label}`}
            icon={XIcon}
            label={`Remove ${label}`}
            onClick={handleRemove}
            size="extra-small"
          />
        ) : null}
      </span>
    </Chip>
  );
});

WorkspaceAgentTagChip.displayName = 'WorkspaceAgentTagChip';
